import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import {
  PLAN_SELECTION_ROUTE,
  SUBSCRIPTION_LOCKED_ROUTE,
} from '../guards/subscription/subscription.guard';
import { OrganizationService } from '../services/organization/organization.service';
import { PermissionService } from '../services/permission/permission.service';
import { Permission } from '../enums/permission.enum';

/**
 * HTTP interceptor that handles subscription errors.
 * On a 402 (Payment Required) response the organization has no active subscription,
 * so the user is redirected to the plan-selection screen - or, when they cannot
 * manage billing themselves, to the locked screen ("ask your administrator").
 * Acts as a safety net for the case where a subscription expires mid-session,
 * after route guards have already run.
 */
export const subscriptionErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const organizationService = inject(OrganizationService);
  const permissionService = inject(PermissionService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 402 &&
        !router.url.startsWith('/onboarding')
      ) {
        const org = organizationService.organization();
        const canManageBilling =
          !org || permissionService.canWithOrg(org, Permission.BillingsManage);
        void router.navigateByUrl(
          canManageBilling ? PLAN_SELECTION_ROUTE : SUBSCRIPTION_LOCKED_ROUTE,
        );
      }

      return throwError(() => error);
    }),
  );
};
